<template>
  <BaseTopBar
    v-if="isMounted"
    v-hotkey="shortcuts"
    :tint="tint"
    :left-panel-width="leftPanelWidth"
    :is-navigation-displayed="isNavigationDisplayed"
    :panel="panel"
    is-hideable
    :with-find="!event.draft"
    @find="$emit('find')"
  >
    <template #left>
      <BaseButton
        kind="auxiliary"
        :tint="tint"
        :text="formatRelativeDate(event.start)"
        @click="goToEventDay"
      />
    </template>
    <template #center>
      <div v-if="event.archived">
        <span>This event is archived.</span>
      </div>
    </template>
    <template #right>
      <slot name="actions" />
      <!-- Main Button -->
      <BaseButton
        v-if="event.editable && !event.draft && mainAction"
        kind="auxiliary"
        :text="mainAction.text"
        :tint="tint"
        @click="getActionFunc(mainAction, $event)"
      >
        <template #icon>
          <component :is="mainAction.icon" />
        </template>
      </BaseButton>
      <ShareButton :export-action="onExport" :tint="tint" />
      <BaseButton
        kind="auxiliary"
        :text="isPinned ? 'Unpin' : 'Pin'"
        @click="togglePin"
      >
        <template #icon>
          <PinIcon :class="{ pinned: isPinned }" />
        </template>
      </BaseButton>
    </template>
    <template v-if="!event.draft" #more>
      <BaseButton
        v-for="action in moreActions"
        :key="action.text"
        class="context-action"
        kind="auxiliary"
        :text="action.text"
        :tint="action.tint || 'none'"
        is-expanded
        @click="getActionFunc(action, $event)"
      >
        <template #icon> <component :is="action.icon" /> </template
        ><template #right>
          {{ action.shortcutsDisplay }}
        </template>
      </BaseButton>
    </template>
  </BaseTopBar>
</template>
<script lang="ts">
import {
  computed,
  defineComponent,
  onBeforeUnmount,
  onMounted,
  PropType,
  ref,
  toRefs,
} from "vue";

import { FullEvent } from "@ctrl/controller-rpc";

import { eventActions } from "@/components/actions/actions";
import BaseButton from "@/components/base/BaseButton.vue";
import BaseTopBar from "@/components/base/BaseTopBar.vue";
import { Panel, Tint } from "@/components/base/types";
import { EventContextActions } from "@/components/context-menu/context-actions";
import { formatRelativeDate } from "@/components/dates";
import { useEventInfo } from "@/components/eventInfo";
import PinIcon from "@/components/icons/PinIcon.vue";
import * as inject from "@/components/inject";
import { createBuiltinExportAction } from "@/components/modals/export";
import { getEventShortcuts } from "@/components/object-shortcuts";
import ShareButton from "@/components/ShareButton.vue";
import {
  assignShortcuts,
  selectAll as selectAllShortcut,
  Shortcuts,
} from "@/components/shortcuts";
import { navigateBackward, navigateToAgenda } from "@/utils/navigate";

export default defineComponent({
  components: {
    BaseButton,
    BaseTopBar,
    PinIcon,
    ShareButton,
  },
  props: {
    hasBlockActive: { type: Boolean, required: true },
    isNavigationDisplayed: { type: Boolean, default: true },
    leftPanelWidth: { type: Number, default: 0 },
    event: { type: Object as PropType<FullEvent>, required: true },
    withRightPanel: { type: Boolean, default: false },
  },
  emits: {
    find: () => true,
    flush: () => true,
    selectAll: () => true,
  },
  async setup(props, { emit }) {
    const controller = inject.controller();
    const logger = inject.logger();
    const profile = inject.profile();
    const calendars = inject.calendars();
    const multipleEventsModal = inject.multipleEventsModal();
    const router = inject.router();
    const isInputFocused = inject.isInputFocused();
    const isModalShown = inject.isModalShown();
    const menu = inject.menu();
    const palette = inject.palette();
    const automationHotkeys = inject.automationHotkeys();
    const simpleModal = inject.simpleModal();
    const flush = () => emit("flush");
    const selectAll = () => emit("selectAll");
    const { event, hasBlockActive, withRightPanel } = toRefs(props);
    const panel = computed<Panel>(() =>
      withRightPanel.value ? Panel.rightPanel : Panel.noPanel
    );
    // FIXME: Mandatory due to BaseTopBar teleportation
    const isMounted = ref(false);
    onMounted(() => (isMounted.value = true));

    // Automation hotkeys run on the object being viewed: arm them with this
    // event while the page is mounted (same input as Mod+K → Run automation).
    const deactivateAutomationHotkeys = automationHotkeys.value?.activate(
      () => ({
        kind: "reference",
        referenceId: { kind: "event", id: event.value.id },
      })
    );
    onBeforeUnmount(() => deactivateAutomationHotkeys?.());

    const tint = computed<Tint>(() =>
      event.value.isArchived ? "red" : "none"
    );
    const eventInfo = useEventInfo(controller, calendars, event);
    onBeforeUnmount(() =>
      eventInfo.then((eventInfo) => eventInfo.releaseEventInfo())
    );
    const { calendar, completable, isMeetingAccepted, isWritable } =
      await eventInfo;
    // A computed is used here in order to allow setting it to undefined.
    //
    // We want to keep the mainAction variable in case we want to later
    // define a main action.
    const mainAction = computed<EventContextActions | undefined>(
      () => undefined
    );
    // The "..." menu is hidden altogether on drafts (see the template).
    const moreActions = computed<EventContextActions[]>(() => {
      // Like getEventShortcuts, gate mutating actions on the calendar's
      // writability (can we write to our copy of the event), not on
      // `editable` (can we modify the organizer-owned fields): an invitee
      // may still complete, mute, or delete their own copy of a meeting.
      return [
        ...(event.value.attendable ? [eventActions.openConference] : []),
        ...(completable.value ? [eventActions.complete] : []),
        ...(isWritable.value
          ? [
              event.value.active
                ? eventActions.setAsPassive
                : eventActions.setAsActive,
            ]
          : []),
        eventActions.runAutomation,
        eventActions.copyLink,
        ...(isWritable.value
          ? [{ ...eventActions.delete, tint: "red" as const }]
          : []),
      ];
    });
    const getActionFunc = (
      action: EventContextActions,
      mouseEvent?: MouseEvent
    ) =>
      action.action(
        { type: "event", value: event.value },
        {
          controller,
          logger,
          profile,
          router,
          simpleModal,
          multipleEventsModal,
          menu,
          palette,
          clickEvent: mouseEvent,
        },
        {
          beforeAction:
            //TODO: Build a better way to compare, tint screws up action === pageActions.delete
            action.text === eventActions.delete.text
              ? async () => navigateBackward(router, menu)
              : undefined,
        }
      );
    const goToEventDay = () => {
      navigateToAgenda(router, event.value.start);
    };
    const shortcuts = computed<Shortcuts>(() => {
      const injection = { isModalShown, isInputFocused };
      const base = hasBlockActive.value
        ? {}
        : {
            ...assignShortcuts(injection, selectAllShortcut, () => {
              selectAll();
            }),
            ...assignShortcuts(
              injection,
              eventActions.runAutomation.shortcuts,
              () => {
                getActionFunc(eventActions.runAutomation);
              }
            ),
            ...getEventShortcuts(
              { event, isWritable, calendar },
              {
                ...injection,
                controller,
                logger,
                profile,
                router,
                simpleModal,
                multipleEventsModal,
                menu,
                palette,
              },
              {
                flush,
                beforeDeleteAction: async () => navigateBackward(router, menu),
              }
            ),
          };
      // Mod+F (find) is bound by BaseTopBar itself (`with-find`).
      return base;
    });

    const isPinned = computed(() => menu.isItemPinned(event.value.id));
    const togglePin = async () => {
      await menu.toggleObjectPin(event.value.id, "event");
    };

    const onExport = createBuiltinExportAction(controller, profile, () => ({
      title: event.value.title,
      notes: event.value.notes,
      extensionKind: "event",
      extensionRow: event.value.extension_row,
    }));

    return {
      completable,
      eventActions,
      flush,
      formatRelativeDate,
      getActionFunc,
      goToEventDay,
      isMeetingAccepted,
      isMounted,
      mainAction,
      menu,
      moreActions,
      onExport,
      open,
      panel,
      shortcuts,
      tint,
      isPinned,
      togglePin,
    };
  },
});
</script>
<style lang="scss" scoped>
@use "@/styles/context-actions.scss";
</style>
